import { memo, useMemo, useRef, useEffect, useState } from "react";
import { LineChart } from "@mui/x-charts/LineChart";
import { Box } from "@mui/material";

import BszSectionCard from "../../../1_BSZFinderApp/components/sectionCards/BszSectionCard";
import { ChartLegend } from "./components/ChartLegend";

function AskomaChartSection({ viewMode, data = [], zoomLevel = 2 }) {
  const [showFeedin, setShowFeedin] = useState(true);
  const [showAskoma, setShowAskoma] = useState(true);
  const [showTemperature, setShowTemperature] = useState(true);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  const viewLabels = {
    today: "Heute",
    week: "Woche",
    month: "Monat",
    all: "Alle",
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      setContainerWidth(containerRef.current.clientWidth);
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const aggregatedData = useMemo(() => {
    const sorted = [...data].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );

    const pointsPerScreen = Math.max(20, Math.floor(containerWidth / 8));

    const zoomFactor =
      {
        1: 0.2,
        2: 0.4,
        3: 0.6,
        4: Number.MAX_SAFE_INTEGER,
      }[zoomLevel] ?? 2;

    const targetPoints =
      zoomFactor === Number.MAX_SAFE_INTEGER
        ? Number.MAX_SAFE_INTEGER
        : pointsPerScreen * zoomFactor;

    const bucketSize =
      targetPoints === Number.MAX_SAFE_INTEGER
        ? 1
        : Math.max(1, Math.ceil(sorted.length / targetPoints));

    const formatLabel = (date) => {
      if (viewMode === "today") {
        return date
          .toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
          .replace(":", ".");
      }

      if (viewMode === "week") {
        return date.toLocaleString([], {
          weekday: "short",
          hour: "2-digit",
        });
      }

      if (viewMode === "month") {
        return date.toLocaleDateString();
      }

      if (viewMode === "year") {
        return date.toLocaleDateString([], {
          month: "short",
        });
      }

      return date.toLocaleDateString([], {
        month: "short",
        year: "numeric",
      });
    };

    const calculateFeedin = (row) =>
      row.feedin < 0
        ? Math.abs(Number(row.feedin || 0)) + Number(row.heater_load || 0)
        : Number(row.heater_load || 0);

    if (bucketSize === 1) {
      return sorted.map((item) => ({
        label: formatLabel(new Date(item.created_at)),
        temperature: Number(item.temperature || 0),
        heaterLoad: Number(item.heater_load || 0),
        feedin: calculateFeedin(item),
      }));
    }

    const result = [];

    for (let i = 0; i < sorted.length; i += bucketSize) {
      const chunk = sorted.slice(i, i + bucketSize);

      const avg = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;

      result.push({
        label: formatLabel(new Date(chunk[0].created_at)),
        temperature: avg(chunk.map((r) => Number(r.temperature || 0))),
        heaterLoad: avg(chunk.map((r) => Number(r.heater_load || 0))),
        feedin: avg(chunk.map((r) => calculateFeedin(r))),
      });
    }

    return result;
  }, [data, zoomLevel, containerWidth, viewMode]);

  const enableScroll = zoomLevel === 4;

  const chartWidth = enableScroll
    ? Math.max(containerWidth, aggregatedData.length * 8)
    : containerWidth;

  const series = [];

  if (showAskoma) {
    series.push({
      data: aggregatedData.map((d) => d.heaterLoad),
      label: "Askoma Verbrauch",
      color: "#2196F3",
      valueFormatter: (value) => `${(value / 1000).toFixed(2)} kWh`,
    });
  }

  if (showFeedin) {
    series.push({
      data: aggregatedData.map((d) => d.feedin),
      label: "PV Überschuss",
      color: "#4CAF50",
      valueFormatter: (value) => `${(value / 1000).toFixed(2)} kWh`,
    });
  }

  if (showTemperature) {
    series.push({
      data: aggregatedData.map((d) => d.temperature * 100),
      label: "Temperatur",
      color: "#FF9800",
      valueFormatter: (value) => `${(value / 100).toFixed(1)} °C`,
    });
  }

  return (
    <BszSectionCard
      // title={"Überblick · " + viewLabels[viewMode]}
      title={"Überblick"}
      defaultExpanded
    >
      <Box sx={{ pb: 10 }} ref={containerRef}>
        <ChartLegend
          showTemperature={showTemperature}
          setShowTemperature={setShowTemperature}
          showAskoma={showAskoma}
          setShowAskoma={setShowAskoma}
          showFeedin={showFeedin}
          setShowFeedin={setShowFeedin}
        />

        <Box
          sx={{
            width: "100%",
            overflowX: enableScroll ? "auto" : "hidden",
          }}
        >
          <Box sx={{ width: chartWidth }}>
            <LineChart
              height={300}
              slotProps={{ legend: { hidden: true } }}
              xAxis={[
                {
                  scaleType: "point",
                  data: aggregatedData.map((d) => d.label),
                },
              ]}
              series={series}
            />
          </Box>
        </Box>
      </Box>
    </BszSectionCard>
  );
}

export default memo(AskomaChartSection);
